import React, { useRef, useState, useEffect } from "react";
import axios from "axios";

// 스크롤 진입 시 한 번만 애니메이션 트리거하는 훅
const useInView = (threshold = 0.2) => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold }
    );

    observer.observe(el);

    return () => {
      observer.disconnect();
    };
  }, [threshold]);

  return [ref, inView];
};

const Contact = () => {
  const [captcha, setCaptcha] = useState(generateCaptcha());
  const [bgColor, setBgColor] = useState(getRandomColor());
  const [formData, setFormData] = useState({
    title: "",
    name: "",
    email: "",
    phone: "",
    content: "",
    file: null,
    captchaInput: "",
    partnershipInquiry: false,
    recruitmentInquiry: false,
    otherInquiry: false,
  });
  const [captchaValid, setCaptchaValid] = useState(false);
  const [agreeToPrivacy, setAgreeToPrivacy] = useState(false);
  const [termsContent, setTermsContent] = useState("");
  const [captchaImage, setCaptchaImage] = useState("");
  const fileInputRef = useRef(null);

  // 애니메이션용 훅 (상단 텍스트+타이틀 / 폼 카드) — 메인배너는 제외
  const [textRef, textInView] = useInView(0.25);
  const [formRef, formInView] = useInView(0.2);

  // ----- 약관 불러오기 -----
  useEffect(() => {
    fetchTerms(2);
  }, []);

  const fetchTerms = async (type) => {
    try {
      const response = await axios.get(
        "https://www.medicode.co.kr:8445/api/get/footer_terms",
        { params: { type } }
      );
      setTermsContent(response.data?.[0]?.contents || "");
    } catch (error) {
      console.error("Error fetching terms:", error);
      setTermsContent("<p>약관을 불러오지 못했습니다.</p>");
    }
  };

  useEffect(() => {
    // captcha / bgColor 바뀔 때마다 캡차 이미지 다시 그림
    const img = generateCaptchaImage(captcha, bgColor);
    setCaptchaImage(img);
  }, [captcha, bgColor]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      partnershipInquiry: false,
      recruitmentInquiry: false,
      otherInquiry: false,
      [name]: checked,
    }));
  };

  const handleFileChange = (e) => {
    setFormData((prev) => ({ ...prev, file: e.target.files[0] }));
  };

  const handleRefreshCaptcha = () => {
    setCaptcha(generateCaptcha());
    setBgColor(getRandomColor());
    setCaptchaValid(false);
  };

  const handleCaptchaVerification = () => {
    if (formData.captchaInput === captcha) {
      setCaptchaValid(true);
      alert("자동등록방지 입력이 확인되었습니다.");
    } else {
      alert("자동등록방지 코드를 확인해 주세요.");
      setFormData((prev) => ({ ...prev, captchaInput: "" }));
      handleRefreshCaptcha();
      document.getElementsByName("captchaInput")[0]?.focus();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { title, name, email, content, file, phone } = formData;

    if (!title) {
      alert("제목을 입력해 주세요.");
      document.getElementsByName("title")[0]?.focus();
      return;
    }
    if (
      !formData.partnershipInquiry &&
      !formData.recruitmentInquiry &&
      !formData.otherInquiry
    ) {
      alert("문의 구분을 하나 이상 선택해 주세요.");
      return;
    }
    if (!name) {
      alert("이름을 입력해 주세요.");
      document.getElementsByName("name")[0]?.focus();
      return;
    }
    if (!email) {
      alert("이메일을 입력해 주세요.");
      document.getElementsByName("email")[0]?.focus();
      return;
    }
    if (!content) {
      alert("내용을 입력해 주세요.");
      document.getElementsByName("content")[0]?.focus();
      return;
    }
    if (!captchaValid) {
      alert("자동등록방지 코드를 확인해 주세요.");
      document.getElementsByName("captchaInput")[0]?.focus();
      return;
    }
    if (!agreeToPrivacy) {
      alert("개인정보 수집 및 이용에 동의해 주세요.");
      return;
    }

    const inquiries = [];
    if (formData.partnershipInquiry) inquiries.push("1");
    if (formData.recruitmentInquiry) inquiries.push("2");
    if (formData.otherInquiry) inquiries.push("3");
    const inquiriesString = inquiries.join(", ");

    try {
      const formDataToSend = new FormData();
      formDataToSend.append("title", title);
      formDataToSend.append("name", name);
      formDataToSend.append("email", email);
      formDataToSend.append("phone", phone);
      formDataToSend.append("content", content);
      formDataToSend.append("inquiries", inquiriesString);
      if (file) formDataToSend.append("file", file);

      await axios.post(
        "https://www.medicode.co.kr:8445/api/post/contact_write",
        formDataToSend,
        {
          headers: { "Content-Type": "multipart/form-data" },
        }
      );
      alert("문의가 완료되었습니다.\n문의 답변은 개별연락을 확인해주세요.");

      setFormData({
        title: "",
        name: "",
        email: "",
        phone: "",
        content: "",
        captchaInput: "",
        partnershipInquiry: false,
        recruitmentInquiry: false,
        otherInquiry: false,
      });
      handleRefreshCaptcha();
      setAgreeToPrivacy(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    } catch (error) {
      console.error("Error sending inquiry:", error);
      alert("문의 전송에 실패했습니다. 다시 시도해 주세요.");
    }
  };

  return (
    <div className="contact-wrap">
      {/* 상단 히어로 영역 - 애니메이션 없음, 고정 표시 */}
      <div className="contact-top">
        <div className="page-tit">
          <div className="contact-wrapper">
            <h3>함께 성장하며, 더 큰 가치를 만들어갑니다.</h3>
            <h2>CONTACT US</h2>
          </div>
        </div>
        <div className="img-wrap"></div>
      </div>

      <div className="content-box">
        {/* 상단 카피 영역 - 스크롤로 진입 시 페이드업 */}
        <div
          className={`contact-txt-wrap fade-section ${
            textInView ? "is-visible" : ""
          }`}
          ref={textRef}
        >
          <h2>
            Connect with <span>MEDICODE.</span>
          </h2>
          <p className="desc1">
            저희와의 만남은 곧 새로운 가능성의 시작입니다.
          </p>
          <p className="desc2">
            함께 성장하며 미래를 만들어갈 소중한 인연을 기다립니다.
          </p>
          <p className="desc3">
            협력, 채용, 그 외의 문의도 정성껏 답변드리겠습니다.
          </p>
        </div>

        <div
          className={`contact-divider fade-section ${
            textInView ? "is-visible" : ""
          }`}
        ></div>

        <div
          className={`contact-us-input fade-section ${
            textInView ? "is-visible" : ""
          }`}
        >
          <p>문의하기</p>
          <div className="alpha-img"></div>
        </div>

        {/* 폼 카드 전체 - 아래로 스크롤 시 페이드업 */}
        <div
          className={`border-box fade-section ${
            formInView ? "is-visible" : ""
          }`}
          ref={formRef}
        >
          <form onSubmit={handleSubmit}>
            <div className="form-grid">
              {/* 1행 */}
              <div className="form-item">
                <label>
                  제목<span className="concas">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  placeholder="제목을 입력하세요"
                  value={formData.title}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-item">
                <label>
                  구분<span className="concas">*</span>
                </label>
                <div className="checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      className="ui-checkbox"
                      name="partnershipInquiry"
                      checked={formData.partnershipInquiry}
                      onChange={handleCheckboxChange}
                    />
                    <span>제휴문의</span>
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      className="ui-checkbox"
                      name="recruitmentInquiry"
                      checked={formData.recruitmentInquiry}
                      onChange={handleCheckboxChange}
                    />
                    <span>채용문의</span>
                  </label>
                  <label>
                    <input
                      type="checkbox"
                      className="ui-checkbox"
                      name="otherInquiry"
                      checked={formData.otherInquiry}
                      onChange={handleCheckboxChange}
                    />
                    <span>기타문의</span>
                  </label>
                </div>
              </div>

              {/* 2행 */}
              <div className="form-item">
                <label>
                  이름<span className="concas">*</span>
                </label>
                <input
                  type="text"
                  name="name"
                  placeholder="이름을 입력하세요"
                  value={formData.name}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-item">
                <label>
                  이메일<span className="concas">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  placeholder="이메일을 입력하세요"
                  value={formData.email}
                  onChange={handleInputChange}
                />
              </div>

              {/* 3행 */}
              <div className="form-item">
                <label>연락처</label>
                <input
                  type="text"
                  name="phone"
                  placeholder="연락처를 입력하세요 ex) 01012341234"
                  value={formData.phone}
                  onChange={handleInputChange}
                />
              </div>
              <div className="form-item">
                <label>첨부파일</label>
                <div className="file-upload">
                  <input
                    type="file"
                    id="fileInput"
                    onChange={handleFileChange}
                    ref={fileInputRef}
                  />
                  <label htmlFor="fileInput" className="file-label">
                    파일 선택
                  </label>
                  <span className="file-name">
                    {fileInputRef.current?.files?.[0]?.name ||
                      "선택된 파일 없음"}
                  </span>
                </div>
              </div>

              {/* 4행 - 내용 */}
              <div className="form-item full">
                <label>
                  내용<span className="concas">*</span>
                </label>
                <textarea
                  name="content"
                  placeholder="내용을 입력하세요 (최대 200글자)"
                  value={formData.content}
                  onChange={handleInputChange}
                  maxLength={200}
                />
              </div>

              {/* 약관 (인라인) */}
              <div className="privacy-box full">
                <h4>개인정보 수집 및 이용약관</h4>
                <div
                  className="privacy-content"
                  dangerouslySetInnerHTML={{ __html: termsContent }}
                />
              </div>
            </div>

            <div className="form-item full">
              <label>
                자동등록방지<span className="concas">*</span>
              </label>
              <div className="captcha">
                <div className="captcha-image">
                  {captchaImage && (
                    <img src={captchaImage} alt="자동등록방지 이미지" />
                  )}
                </div>

                <input
                  type="text"
                  name="captchaInput"
                  className="captcha-input"
                  placeholder="이미지에 보이는 숫자를 입력하세요."
                  value={formData.captchaInput}
                  onChange={handleInputChange}
                />

                <div className="captcha-btns">
                  <button
                    type="button"
                    className="refresh-btn"
                    onClick={handleRefreshCaptcha}
                  >
                    새로고침
                  </button>
                  <button
                    type="button"
                    className="check-s-btn"
                    onClick={handleCaptchaVerification}
                  >
                    확인
                  </button>
                </div>
              </div>
            </div>

            {/* 제출 영역 */}
            <div className="submit-wrap">
              <label>
                <input
                  type="checkbox"
                  className="ui-checkbox"
                  checked={agreeToPrivacy}
                  onChange={() => setAgreeToPrivacy(!agreeToPrivacy)}
                />
                <span>개인정보 수집 및 이용에 동의합니다.</span>
              </label>
              <div className="center-btn">
                <button type="submit">문의하기</button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Contact;

function generateCaptcha() {
  return Math.floor(100000 + Math.random() * 900000).toString();
}
function getRandomColor() {
  return "#ccc";
}

const generateCaptchaImage = (captcha, bgColor) => {
  const canvas = document.createElement("canvas");
  canvas.width = 150;
  canvas.height = 30;
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = bgColor;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.font = "18px Arial";
  ctx.fillStyle = "black";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(captcha, canvas.width / 2, canvas.height / 2);
  return canvas.toDataURL();
};
